import { cn, formatRank } from "@/lib/utils";
import { Check, Image as ImageIcon, Link2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, fetchSharedScout, scoutTeam, syncRoster } from "../api";
import type { DraftRec, ScoutChampRecord, ScoutPlayer, ScoutReport, ScoutResult, ScoutTeamGame } from "../types";
import { getChampionIconUrl, getChampionName } from "../utils/ddragon";
import { getSavedRegion, parseRoster, REGIONS, saveRegion, summonerPath } from "../utils/regions";
import { renderScoutImage } from "../utils/scoutImage";
import { formatDuration, winrateColor } from "./summoner/parts";
import { button, label, Skeleton } from "./ui/primitives";

const MAX_ROSTER = 7; // same cap as the API
const ROSTER_KEY = "riftwatch.scout";
const OWN_KEY = "riftwatch.scout.own";
const ROLES_KEY = "riftwatch.scout.roles";
export const ROLE: Record<string, string> = { TOP: "Top", JUNGLE: "Jungle", MIDDLE: "Mid", BOTTOM: "Bot", UTILITY: "Support" };
const ROLE_ORDER = Object.keys(ROLE);
export const card = "rounded-[10px] border border-line bg-surface p-3.5";
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
const short = (riotId: string) => riotId.split("#")[0];
/** Lane records worth a look: 2+ games and clearly won or clearly lost (same cut as the report). */
const notable = (vs: ScoutChampRecord[] = []) => vs.filter(v => v.games >= 2 && (v.wins * 3 <= v.games || v.wins * 4 >= v.games * 3)).slice(0, 4);

const saved = (key: string) => {
	try {
		return localStorage.getItem(key) ?? "";
	} catch {
		return "";
	}
};
type Roles = Record<string, string>; // "name#tag" lowercased -> role
const idKey = (r: { name: string; tag: string }) => `${r.name}#${r.tag}`.toLowerCase();
const savedRoles = (): Roles => {
	try {
		return JSON.parse(saved(ROLES_KEY) || "{}");
	} catch {
		return {};
	}
};
const rolesOf = (players: ScoutPlayer[]): Roles => Object.fromEntries(players.flatMap(p => (p.startRole ?? p.role ? [[p.riotId.toLowerCase(), (p.startRole ?? p.role)!]] : [])));
const rankText = (p: ScoutPlayer) => (p.rank ? formatRank(p.rank) : "Unranked");

function Bullets({ title, lines }: { title: string; lines: string[] }) {
	if (!lines.length) return null;
	return (
		<div>
			<h3 className={cn(label, "m-0 mb-2 text-signal")}>{title}</h3>
			<ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-sm leading-relaxed text-ink-2">
				{lines.map((l, i) => (
					<li key={i} className="flex gap-2">
						<span aria-hidden="true" className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-ink-3" />
						<span>{l}</span>
					</li>
				))}
			</ul>
		</div>
	);
}

/** One step of the draft plan as champion cards: icon, name, whose champion it is, why. Bans are numbered by priority. */
function DraftStep({ step, title, hint, recs, ban, first }: { step?: number; title: string; hint: string; recs: DraftRec[]; ban: boolean; first?: number }) {
	if (!recs.length) return null;
	return (
		<li className="rounded-lg border border-line bg-raised p-2.5">
			<div className="mb-2 flex items-baseline gap-2">
				{step && <span className="font-mono text-[11px] font-bold text-ink-3">{step}</span>}
				<h4 className={cn(label, "m-0", ban ? "text-signal" : "text-ink")}>{title}</h4>
				<span className="text-xs text-ink-3">{hint}</span>
			</div>
			<ul className="m-0 flex list-none flex-col gap-2.5 p-0">
				{recs.map((r, i) => (
					<li key={r.championId} className="flex gap-2.5">
						<span className="relative shrink-0">
							<img src={getChampionIconUrl(r.championId)} alt="" loading="lazy" className={cn("h-12 w-12 rounded-[9px]", ban && "grayscale-[.35]")} />
							{ban && (
								<span className="absolute -left-1.5 -top-1.5 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-signal font-mono text-[10px] font-bold text-on-signal">
									{i + 1}
								</span>
							)}
						</span>
						<span className="min-w-0">
							<span className="block truncate text-sm font-bold">
								{getChampionName(r.championId)}
								{r.player && <span className="ml-1.5 text-xs font-medium text-ink-3">{r.player.split("#")[0]}</span>}
								{first === r.championId && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-[.08em] text-signal">First pick</span>}
							</span>
							<span className="mt-0.5 block text-xs leading-normal text-ink-2">{r.reason}</span>
						</span>
					</li>
				))}
			</ul>
		</li>
	);
}

/** Tournament draft order: ban 3, pick 3, ban 2, pick 2. With our own roster a second row shows our side of the draft. */
function DraftPlan({ data, report }: { data: ScoutResult; report: ScoutReport }) {
	if (!report.bans1.length && !report.picks1.length) return null;
	const theirBans = report.theirBans ?? [];
	const ourPicks = report.ourPicks ?? [];
	const contested = data.matchup?.contested ?? [];
	const flex = data.team.flex ?? [];
	const side = data.matchup?.side;
	const synergies = data.matchup?.synergies ?? [];
	return (
		<div>
			<h3 className={cn(label, "m-0 mb-2 text-signal")}>Draft plan · tournament draft</h3>
			<ol className="m-0 grid list-none gap-2 p-0 sm:grid-cols-2 xl:grid-cols-4">
				<DraftStep step={1} title="Ban phase 1" hint="your 3 bans" recs={report.bans1} ban />
				<DraftStep step={2} title="Pick phase 1" hint="expect from them" recs={report.picks1} ban={false} />
				<DraftStep step={3} title="Ban phase 2" hint="your 2 bans" recs={report.bans2} ban />
				<DraftStep step={4} title="Pick phase 2" hint="expect from them" recs={report.picks2} ban={false} />
			</ol>
			{(theirBans.length > 0 || ourPicks.length > 0) && (
				<ol className="m-0 mt-2 grid list-none gap-2 p-0 sm:grid-cols-2">
					<DraftStep title="Their likely bans" hint="expect against you" recs={theirBans} ban />
					<DraftStep title="Your picks" hint="one per player" recs={ourPicks} ban={false} first={report.firstPick?.championId} />
				</ol>
			)}
			{report.firstPick && (
				<p className="m-0 mt-2.5 text-[13px] leading-normal text-ink-2">
					<span className="font-bold text-ink">First pick on blue side: {getChampionName(report.firstPick.championId)}.</span> {report.firstPick.reason}
				</p>
			)}
			{(contested.length > 0 || flex.length > 0 || synergies.length > 0 || side?.recommended) && (
				<div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink-2">
					{side?.recommended && (
						<span>
							<span className={cn("font-bold", side.recommended === "blue" ? "text-side-blue" : "text-side-red")}>Take {side.recommended} side</span>: {side.reason}
						</span>
					)}
					{contested.length > 0 && (
						<span className="flex items-center gap-1.5">
							Contested <Champs ids={contested.map(c => c.championId)} size={22} />
						</span>
					)}
					{flex.length > 0 && (
						<span className="flex items-center gap-1.5">
							Their flex picks <Champs ids={flex.map(f => f.championId)} size={22} />
						</span>
					)}
					{synergies.map(x => (
						<span key={x.championIds.join("+")} className="flex items-center gap-1.5" title={`${short(x.players[0])} + ${short(x.players[1])}, high-elo duo win rate`}>
							Synergy <Champs ids={x.championIds} size={22} />
							<span className={cn("font-bold", winrateColor(pct(x.wins, x.games)))}>{pct(x.wins, x.games)}%</span>
							<span className="text-ink-3">{x.games}g</span>
						</span>
					))}
				</div>
			)}
		</div>
	);
}

function LaneSide({ p }: { p?: ScoutPlayer }) {
	if (!p) return <div className="text-[13px] text-ink-3">No starter found</div>;
	return (
		<div className="min-w-0">
			<div className="truncate text-sm font-bold">
				{p.riotId.split("#")[0]} <span className="text-xs font-medium text-ink-3">{rankText(p)}</span>
			</div>
			<div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
				<Champs ids={p.champions.slice(0, 3).map(c => c.championId)} size={22} />
				<span className="whitespace-nowrap text-xs text-ink-3">
					{p.kda} KDA · {p.csPerMin} CS/min · pool {p.poolDepth}
				</span>
			</div>
		</div>
	);
}

/** Position by position: their starter against ours, the champions both play, and how to play it. */
function Lanes({ data }: { data: ScoutResult }) {
	if (!data.matchup || !data.own) return null;
	const own = data.own;
	const cols = "grid gap-x-4 gap-y-1.5 sm:grid-cols-[64px_minmax(0,1fr)_minmax(0,1fr)]";
	return (
		<section className={card} aria-label="Lane matchups">
			<div className={cn(cols, "mb-1 items-baseline")}>
				<h2 className={cn(label, "m-0")}>Lanes</h2>
				<span className={cn(label, "hidden sm:block")}>Them</span>
				<span className={cn(label, "hidden sm:block")}>You</span>
			</div>
			<ul className="m-0 list-none p-0">
				{data.matchup.lanes.map(l => {
					const note = data.report?.lanes?.find(x => x.role === l.role)?.note;
					return (
						<li key={l.role} className={cn(cols, "border-t border-line py-2.5 first:border-t-0")}>
							<span className="text-[13px] font-bold">{ROLE[l.role] ?? l.role}</span>
							<LaneSide p={data.players.find(p => p.riotId === l.theirs)} />
							<LaneSide p={own.players.find(p => p.riotId === l.ours)} />
							{(note || l.contested.length > 0) && (
								<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] leading-normal text-ink-2 sm:col-span-2 sm:col-start-2">
									{l.contested.length > 0 && (
										<span className="flex items-center gap-1.5 text-xs text-ink-3">
											Both play <Champs ids={l.contested} size={20} />
										</span>
									)}
									{note}
								</div>
							)}
						</li>
					);
				})}
			</ul>
		</section>
	);
}

/** High-elo counters to their starters' main champions; a counter one of our starters plays is marked. */
function Counters({ data }: { data: ScoutResult }) {
	const rows = data.counters ?? [];
	if (!rows.length) return null;
	return (
		<section className={card} aria-label="Counter picks">
			<h2 className={cn(label, "m-0 mb-1")}>Counter picks</h2>
			<p className="m-0 mb-2 text-xs text-ink-3">Lane win rates from Challenger and Grandmaster solo queue, current and last patch. General champion strength, not this team.</p>
			<ul className="m-0 list-none p-0">
				{rows.map(r => (
					<li key={`${r.role}:${r.championId}`} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-line py-2 text-[13px] first:border-t-0">
						<span className="w-14 font-bold">{ROLE[r.role] ?? r.role}</span>
						<span className="flex min-w-[150px] items-center gap-1.5">
							<Champs ids={[r.championId]} />
							<span className="font-semibold">{getChampionName(r.championId)}</span>
							<span className="text-xs text-ink-3">{short(r.riotId)}</span>
						</span>
						<span className="flex flex-wrap items-center gap-x-3 gap-y-1">
							{r.counters.map(c => (
								<span key={c.championId} className="flex items-center gap-1.5">
									<Champs ids={[c.championId]} size={22} />
									<span className="font-semibold">{getChampionName(c.championId)}</span>
									<span className={cn("font-bold", winrateColor(pct(c.wins, c.games)))}>{pct(c.wins, c.games)}%</span>
									<span className="text-xs text-ink-3">{c.games}g</span>
									{c.player && <span className="text-[10px] font-bold uppercase tracking-[.08em] text-signal">{short(c.player)} plays it</span>}
								</span>
							))}
						</span>
					</li>
				))}
			</ul>
		</section>
	);
}

/** Champion icons with a record each, e.g. lane opponents or enemy champions. */
function Records({ recs }: { recs: ScoutChampRecord[] }) {
	return (
		<span className="flex flex-wrap gap-x-3 gap-y-1">
			{recs.map(r => (
				<span key={r.championId} className="flex items-center gap-1.5 text-xs">
					<Champs ids={[r.championId]} size={20} />
					<span className={cn("font-bold", winrateColor(pct(r.wins, r.games)))}>
						{r.wins}W {r.games - r.wins}L
					</span>
				</span>
			))}
		</span>
	);
}

function Report({ data, report }: { data: ScoutResult; report: ScoutReport }) {
	return (
		<div className="flex flex-col gap-4">
			{report.overview.length > 0 && <p className="m-0 max-w-[820px] text-[15px] leading-relaxed text-ink">{report.overview.join(" ")}</p>}
			{report.ai === false && (
				<p className="m-0 text-sm text-ink-2">
					The written report is not available right now. The draft plan below comes from the numbers alone; try again in a moment for the full report.
				</p>
			)}
			<DraftPlan data={data} report={report} />
			<div className="grid gap-4 md:grid-cols-2">
				<Bullets title="On blue side" lines={report.blueSide ?? []} />
				<Bullets title="On red side" lines={report.redSide ?? []} />
				<Bullets title="Your comp" lines={[...(report.comp ?? []), ...(report.compCheck ?? [])]} />
				<Bullets title="Their expected comp" lines={report.theirComp ?? []} />
			</div>
			<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
				<Bullets title="How they win" lines={report.winConditions ?? []} />
				<Bullets title="Draft tendencies" lines={report.draft} />
				<Bullets title="Playstyle" lines={report.playstyle} />
				<Bullets title="Game plan" lines={report.gamePlan} />
			</div>
		</div>
	);
}

export function Champs({ ids, size = 26 }: { ids: number[]; size?: number }) {
	return (
		<span className="flex flex-wrap gap-1">
			{ids.map((id, i) => (
				<img key={i} src={getChampionIconUrl(id)} alt={getChampionName(id)} title={getChampionName(id)} loading="lazy" style={{ width: size, height: size }} className="rounded-[5px]" />
			))}
		</span>
	);
}

export function BanList({ title, bans }: { title: string; bans: { championId: number; count: number }[] }) {
	return (
		<div>
			<div className={cn(label, "mb-2")}>{title}</div>
			{bans.length ? (
				<ul className="m-0 flex list-none flex-wrap gap-x-3 gap-y-2 p-0">
					{bans.map(b => (
						<li key={b.championId} className="flex items-center gap-1.5 text-[13px]">
							<img src={getChampionIconUrl(b.championId)} alt="" loading="lazy" className="h-7 w-7 rounded-[6px]" />
							<span className="font-semibold">{getChampionName(b.championId)}</span>
							<span className="font-mono text-xs text-ink-3">×{b.count}</span>
						</li>
					))}
				</ul>
			) : (
				<div className="text-[13px] text-ink-3">No bans recorded</div>
			)}
		</div>
	);
}

export function PlayerCard({ p, region, ai }: { p: ScoutPlayer; region: string; ai?: ScoutReport["players"][number] }) {
	const [name = "", tag = ""] = p.riotId.split("#");
	const watch = new Set(ai?.championIds);
	const role = p.startRole ?? p.role;
	return (
		<div className={card}>
			<div className="mb-2.5 flex items-baseline justify-between gap-2">
				<Link to={summonerPath(region, name, tag)} className="min-w-0 truncate text-sm font-bold hover:text-signal">
					{name}
					<span className="font-medium text-ink-3">#{tag}</span>
				</Link>
				<span className="shrink-0 text-xs text-ink-3">
					{p.startRole === null && "Stand-in · "}
					{role ? ROLE[role] ?? role : "—"}
				</span>
			</div>
			<div className="mb-2.5 text-xs text-ink-2">
				{p.rank ? `${formatRank(p.rank)}${/LP$/.test(formatRank(p.rank)) ? "" : ` · ${p.rank.leaguePoints} LP`}` : "Unranked"} · {p.games} games
				{p.poolDepth !== undefined && (
					<span className="mt-0.5 block text-ink-3">
						{p.kda} KDA · {p.csPerMin} CS/min · {p.dmgPerMin} dmg/min · pool depth {p.poolDepth}
					</span>
				)}
			</div>
			{ai && <p className="m-0 mb-2.5 border-l-2 border-signal pl-2.5 text-[13px] leading-normal text-ink-2">{ai.note}</p>}
			{notable(p.laneVs).length > 0 && (
				<div className="mb-2.5 flex flex-wrap items-center gap-2 text-xs text-ink-3">
					In lane vs <Records recs={notable(p.laneVs)} />
				</div>
			)}
			{p.champions.length ? (
				<ul className="m-0 flex list-none flex-col gap-1 p-0">
					{p.champions.map(c => (
						<li key={c.championId} className="grid grid-cols-[26px_minmax(0,1fr)_auto_40px] items-center gap-2 text-[13px]">
							<img
								src={getChampionIconUrl(c.championId)}
								alt=""
								loading="lazy"
								className={cn("h-[26px] w-[26px] rounded-[5px]", watch.has(c.championId) && "outline outline-2 outline-offset-1 outline-signal")}
							/>
							<span className="truncate font-semibold">
								{getChampionName(c.championId) === "Unknown" ? c.championName : getChampionName(c.championId)}
								{watch.has(c.championId) && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-[.08em] text-signal">Watch</span>}
							</span>
							<span className="whitespace-nowrap text-xs text-ink-3">
								{c.games}g{c.teamGames ? ` · ${c.teamGames} team` : ""} · {c.kda} KDA
							</span>
							<span className={cn("text-right font-bold", winrateColor(pct(c.wins, c.games)))}>{pct(c.wins, c.games)}%</span>
						</li>
					))}
				</ul>
			) : (
				<div className="text-[13px] text-ink-3">No recent ranked or team games</div>
			)}
		</div>
	);
}

function TeamGameRow({ g }: { g: ScoutTeamGame }) {
	const picks = [...g.picks].sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role));
	return (
		<li className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-line px-1 py-2 text-xs text-ink-3 first:border-t-0">
			<span className={cn("w-10 font-extrabold", g.win ? "text-signal" : "text-ink-2")}>{g.win ? "Win" : "Loss"}</span>
			<span className={cn("w-9 font-semibold", g.side === "blue" ? "text-side-blue" : "text-side-red")}>{g.side === "blue" ? "Blue" : "Red"}</span>
			<Champs ids={picks.map(p => p.championId)} />
			<span className="flex items-center gap-1.5">
				Bans <Champs ids={g.bans} size={20} />
			</span>
			<span className="ml-auto whitespace-nowrap">
				{g.kind === "tournament" ? "Tournament" : g.kind === "flex" ? "Flex" : "Clash"} · <span className="font-mono">{formatDuration(g.duration)}</span> ·{" "}
				{new Date(g.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
			</span>
		</li>
	);
}

/** Copies the /scout/<id> link: anyone with it sees this exact result, without scouting again. */
function ShareButton({ id }: { id: string }) {
	const [copied, setCopied] = useState(false);
	const copy = async () => {
		try {
			await navigator.clipboard.writeText(`${location.origin}/scout/${id}`);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			/* clipboard blocked: the link is in the address bar */
		}
	};
	return (
		<button type="button" onClick={copy} className={cn(button.secondary, "h-8 px-3 text-xs")}>
			{copied ? <Check aria-hidden="true" className="h-3.5 w-3.5 text-signal" /> : <Link2 aria-hidden="true" className="h-3.5 w-3.5" />}
			<span aria-live="polite">{copied ? "Link copied" : "Copy link"}</span>
		</button>
	);
}

/** The report as a picture to post in Discord: preview, copy to the clipboard (paste into a channel) or download. */
function DiscordImage({ data }: { data: ScoutResult }) {
	const [image, setImage] = useState<{ blob: Blob; url: string } | null>(null);
	const [note, setNote] = useState("Drawing the image…");

	useEffect(() => {
		let url = "";
		let cancelled = false;
		renderScoutImage(data)
			.then(blob => {
				if (cancelled) return;
				url = URL.createObjectURL(blob);
				setImage({ blob, url });
				setNote("");
			})
			.catch(() => !cancelled && setNote("Could not draw the image in this browser."));
		return () => {
			cancelled = true;
			if (url) URL.revokeObjectURL(url);
		};
	}, [data]);

	const copy = async () => {
		if (!image) return;
		try {
			await navigator.clipboard.write([new ClipboardItem({ "image/png": image.blob })]);
			setNote("Image copied. Paste it into Discord with Ctrl+V.");
		} catch {
			setNote("This browser cannot copy images: use Download, or right-click the image.");
		}
	};

	return (
		<div className="mb-4 rounded-lg border border-line bg-raised p-2.5">
			{image && <img src={image.url} alt="Scouting report as an image" className="block w-full rounded-md" />}
			<div className="mt-2.5 flex flex-wrap items-center gap-2.5 first:mt-0">
				<button type="button" onClick={copy} disabled={!image} className={cn(button.primary, "h-9 px-3.5 text-[13px]")}>
					Copy image
				</button>
				{image && (
					<a href={image.url} download={`scout-${data.id ?? "report"}.png`} className={cn(button.secondary, "h-9 text-[13px]")}>
						Download
					</a>
				)}
				<span role="status" className="text-xs text-ink-2">
					{note}
				</span>
			</div>
		</div>
	);
}

function Result({ data }: { data: ScoutResult }) {
	const [showImage, setShowImage] = useState(false);
	const t = data.team;
	const region = data.region;
	const tiles = [
		{ v: `${t.wins}W ${t.games - t.wins}L`, l: `${t.games} team games` },
		{ v: `${pct(t.blue.wins, t.blue.games)}%`, l: `Blue side · ${t.blue.games}` },
		{ v: `${pct(t.red.wins, t.red.games)}%`, l: `Red side · ${t.red.games}` },
		{ v: formatDuration(t.avgDuration), l: "Avg game" },
		{ v: `${t.first.blood}%`, l: "First blood" },
		{ v: `${t.first.tower}%`, l: "First tower" },
		{ v: `${t.first.dragon}%`, l: "First dragon" },
		{ v: `${t.first.baron}%`, l: "First baron" },
		// missing in reports saved before the matchup report existed
		...(t.byLength && t.damage
			? [
					{ v: `${t.first.herald}%`, l: "First herald" },
					{ v: `${t.first.grubs}%`, l: "First grubs" },
					{ v: `${t.damage.physical} / ${t.damage.magic}`, l: "AD / AP damage %" },
					{ v: `${formatDuration(t.winDuration ?? 0)} / ${formatDuration(t.lossDuration ?? 0)}`, l: "Avg win / loss" },
					...[
						{ r: t.byLength.short, l: "Under 25 min" },
						{ r: t.byLength.mid, l: "25 to 35 min" },
						{ r: t.byLength.long, l: "Over 35 min" }
					].map(x => ({ v: `${x.r.wins}W ${x.r.games - x.r.wins}L`, l: x.l }))
				]
			: [])
	];
	// who they play through: by share of the team's damage
	const carries = data.players.filter(p => p.teamGames && p.dmgShare !== undefined).sort((a, b) => b.dmgShare! - a.dmgShare!);
	return (
		<div className="mt-6 flex flex-col gap-4">
			<section className={card} aria-label="Scouting report">
				<div className="mb-3 flex items-baseline justify-between gap-3">
					<h2 className="m-0 text-lg font-extrabold [font-stretch:112%]">Scouting report</h2>
					<span className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1.5 text-xs text-ink-3">
						<span>
							AI-written from the numbers below · {new Date(data.generatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })} · verify before you draft
						</span>
						{data.report && (
							<button type="button" onClick={() => setShowImage(v => !v)} aria-expanded={showImage} className={cn(button.secondary, "h-8 px-3 text-xs")}>
								<ImageIcon aria-hidden="true" className="h-3.5 w-3.5" />
								Discord image
							</button>
						)}
						{data.id && <ShareButton id={data.id} />}
					</span>
				</div>
				{showImage && data.report && <DiscordImage data={data} />}
				{data.report ? <Report data={data} report={data.report} /> : <p className="m-0 text-sm text-ink-2">The report could not be written right now. The numbers below are complete; try again in a moment.</p>}
			</section>

			<Lanes data={data} />

			<Counters data={data} />

			<section className={card} aria-label="Team games">
				<h2 className={cn(label, "m-0 mb-3")}>
					Team games{t.games > 0 && ` · ${t.tournamentGames} tournament`}
				</h2>
				{t.games ? (
					<>
						<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
							{tiles.map(x => (
								<div key={x.l} className="rounded-lg border border-line bg-raised p-2.5">
									<div className="text-lg font-extrabold [font-stretch:112%]">{x.v}</div>
									<div className="mt-px text-xs text-ink-2">{x.l}</div>
								</div>
							))}
						</div>
						<div className="mt-4 grid gap-4 sm:grid-cols-2">
							<BanList title="They ban" bans={t.bans} />
							<BanList title="Banned against them" bans={t.bansAgainst} />
							{!!t.bans1?.length && <BanList title="Their ban phase 1" bans={t.bans1} />}
							{!!t.bans2?.length && <BanList title="Their ban phase 2" bans={t.bans2} />}
						</div>
						{carries.length > 0 && (
							<div className="mt-4">
								<div className={cn(label, "mb-2")}>They play through</div>
								<ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-[13px]">
									{carries.map(p => (
										<li key={p.riotId}>
											<span className="font-semibold">{p.riotId.split("#")[0]}</span>{" "}
											<span className="text-xs text-ink-3">
												{p.dmgShare}% damage · {p.goldShare}% gold
											</span>
										</li>
									))}
								</ul>
							</div>
						)}
						{(!!t.styles?.length || !!t.losesTo?.length || !!t.duos?.length) && (
							<div className="mt-4 grid gap-4 sm:grid-cols-3">
								{!!t.styles?.length && (
									<div>
										<div className={cn(label, "mb-2")}>Comp styles</div>
										<ul className="m-0 flex list-none flex-wrap gap-x-3 gap-y-1 p-0 text-[13px]">
											{t.styles.map(x => (
												<li key={x.style}>
													<span className="font-semibold">{x.style}</span> <span className="text-xs text-ink-3">{x.games}×</span>
												</li>
											))}
										</ul>
									</div>
								)}
								{!!t.losesTo?.length && (
									<div>
										<div className={cn(label, "mb-2")}>They lose to</div>
										<Records recs={t.losesTo} />
									</div>
								)}
								{!!t.duos?.length && (
									<div>
										<div className={cn(label, "mb-2")}>Duos</div>
										<ul className="m-0 flex list-none flex-col gap-1 p-0 text-xs">
											{t.duos.slice(0, 4).map(d => (
												<li key={d.players.join() + d.championIds.join()} className="flex items-center gap-1.5">
													<Champs ids={d.championIds} size={20} />
													<span className="text-ink-3">
														{short(d.players[0])} + {short(d.players[1])}
													</span>
													<span className={cn("font-bold", winrateColor(pct(d.wins, d.games)))}>
														{d.wins}W {d.games - d.wins}L
													</span>
												</li>
											))}
										</ul>
									</div>
								)}
							</div>
						)}
						<ul className="m-0 mt-4 list-none p-0">
							{t.recent.map(g => (
								<TeamGameRow key={g.matchId} g={g} />
							))}
						</ul>
					</>
				) : (
					<p className="m-0 text-sm text-ink-2">
						No games with at least four of these players on one side were found (tournament or Flex). The report is based on each player&apos;s own
						games. Check that the Riot IDs are the accounts they play team games on.
					</p>
				)}
			</section>

			<section aria-label="Players" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
				{data.players.map(p => (
					<PlayerCard key={p.riotId} p={p} region={region} ai={data.report?.players.find(x => x.riotId === p.riotId)} />
				))}
			</section>

			{data.own && (
				<>
					<h2 className={cn(label, "m-0 mt-2")}>Your team</h2>
					<section aria-label="Your team" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
						{data.own.players.map(p => (
							<PlayerCard key={p.riotId} p={p} region={region} />
						))}
					</section>
				</>
			)}
		</div>
	);
}

function RosterInput({ title, value, onChange, roster, roles, setRole }: { title: string; value: string; onChange: (v: string) => void; roster: { name: string; tag: string }[]; roles: Roles; setRole: (key: string, role: string) => void }) {
	const count = roster.length;
	return (
		<div>
			<label className="block">
				<span className="mb-1.5 flex items-baseline justify-between gap-2">
					<span className={label}>{title}</span>
					<span className="text-xs text-ink-3" aria-live="polite">
						{count > MAX_ROSTER ? `At most ${MAX_ROSTER} players (${count} found)` : `${count} Riot ${count === 1 ? "ID" : "IDs"} found`}
					</span>
				</span>
				<textarea
					value={value}
					onChange={e => onChange(e.target.value)}
					rows={6}
					spellCheck={false}
					autoCapitalize="off"
					placeholder={"Name#TAG\nName#TAG\n…or https://www.op.gg/multisearch/euw?summoners=…"}
					className="block w-full resize-y rounded-xl border border-line-2 bg-surface p-3.5 font-mono text-sm text-ink placeholder:text-ink-3 focus:border-signal focus:outline-none"
				/>
			</label>
			{/* the role decides which games count: only champions played on it make the pool */}
			{count > 0 && count <= MAX_ROSTER && (
				<ul className="m-0 mt-2 flex list-none flex-col gap-1.5 p-0">
					{roster.map(r => {
						const k = idKey(r);
						return (
							<li key={k} className="flex items-center justify-between gap-2 text-[13px]">
								<span className="min-w-0 truncate font-semibold">
									{r.name}
									<span className="font-medium text-ink-3">#{r.tag}</span>
								</span>
								<select
									required
									value={roles[k] ?? ""}
									onChange={e => setRole(k, e.target.value)}
									aria-label={`Role of ${r.name}#${r.tag}`}
									className={cn("h-8 cursor-pointer rounded-lg border bg-raised px-2 text-[13px] font-bold", roles[k] ? "border-line-2 text-ink" : "border-signal text-ink-3")}
								>
									<option value="" disabled>
										Role…
									</option>
									{ROLE_ORDER.map(x => (
										<option key={x} value={x}>
											{ROLE[x]}
										</option>
									))}
								</select>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
}

export function ScoutPage() {
	const [region, setRegion] = useState(getSavedRegion);
	const [text, setText] = useState(() => saved(ROSTER_KEY));
	const [ownText, setOwnText] = useState(() => saved(OWN_KEY));
	const [roles, setRoles] = useState(savedRoles);
	const setRole = (k: string, role: string) => setRoles(r => ({ ...r, [k]: role }));
	const [busy, setBusy] = useState(false);
	const [progress, setProgress] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [result, setResult] = useState<ScoutResult | null>(null);
	const { id } = useParams();
	const navigate = useNavigate();

	// A shared link (/scout/<id>): show the saved result. Skipped when it is the one this page just produced.
	useEffect(() => {
		if (!id || result?.id === id) return;
		let cancelled = false;
		setBusy(true);
		setError(null);
		setProgress("Loading the shared report");
		fetchSharedScout(id)
			.then(data => {
				if (cancelled) return;
				setResult(data);
				setText(data.players.map(p => p.riotId).join("\n"));
				if (data.own) setOwnText(data.own.players.map(p => p.riotId).join("\n"));
				setRoles(r => ({ ...r, ...rolesOf([...data.players, ...(data.own?.players ?? [])]) }));
				setRegion(data.region);
			})
			.catch(e => !cancelled && setError(e instanceof ApiError ? e.message : "Could not load this report"))
			.finally(() => !cancelled && setBusy(false));
		return () => {
			cancelled = true;
		};
	}, [id]);
	const roster = parseRoster(text);
	const ownRoster = parseRoster(ownText);
	const valid = roster.length >= 1 && roster.length <= MAX_ROSTER && ownRoster.length <= MAX_ROSTER;
	const all = [...roster, ...ownRoster];
	const missingRoles = all.filter(r => !roles[idKey(r)]).length;

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!valid || missingRoles || busy) return;
		setBusy(true);
		setError(null);
		try {
			localStorage.setItem(ROSTER_KEY, text);
			localStorage.setItem(OWN_KEY, ownText);
			localStorage.setItem(ROLES_KEY, JSON.stringify(roles));
		} catch {
			/* not persisted */
		}
		try {
			// 1. Every player is synced in a request of their own, 2. then the report is written from what is stored now.
			const riotIds = await syncRoster(region, all, setProgress);
			setProgress("Writing the report");
			const roleList = all.map(r => roles[idKey(r)]!);
			const data = await scoutTeam(region, riotIds.slice(0, roster.length), roleList.slice(0, roster.length), riotIds.slice(roster.length), roleList.slice(roster.length));
			setResult(data);
			navigate(data.id ? `/scout/${data.id}` : "/scout"); // the address bar is now the shareable link
		} catch (err) {
			const e2 = err instanceof ApiError ? err : null;
			// players loaded so far are stored, so the retry continues instead of starting over
			setError(e2?.status === 429 ? `Riot is busy. Try again in ${e2.retryAfterSeconds ?? 30}s: the players loaded so far are kept.` : (e2?.message ?? "Could not scout this team"));
		} finally {
			setBusy(false);
		}
	};

	return (
		<section className="pt-8 sm:pt-12">
			<h1 className="m-0 text-[30px] font-extrabold leading-[1.05] [font-stretch:118%] sm:text-[40px]">Scout a team.</h1>
			<p className="mt-3 mb-5 max-w-[640px] text-[15px] leading-normal text-ink-2 text-pretty">
				Paste your Prime League opponent&apos;s roster: the op.gg multi-search link from their team page, or one Riot ID per line. Add your own
				roster and the report covers the matchup: lane by lane, what to ban, what they will ban against you and what to pick.
			</p>

			<form onSubmit={submit} className="max-w-[860px]">
				<div className="grid gap-3 sm:grid-cols-2">
					<RosterInput title="Opponent" value={text} onChange={setText} roster={roster} roles={roles} setRole={setRole} />
					<RosterInput title="Your team (optional)" value={ownText} onChange={setOwnText} roster={ownRoster} roles={roles} setRole={setRole} />
				</div>
				<div className="mt-3 flex flex-wrap items-center gap-3">
					<label className="flex items-center gap-2 text-[13px] text-ink-2">
						Region
						<select
							value={region}
							onChange={e => {
								setRegion(e.target.value);
								saveRegion(e.target.value);
							}}
							className="h-10 cursor-pointer rounded-lg border border-line-2 bg-raised px-2.5 text-[13px] font-bold text-ink"
						>
							{REGIONS.map(r => (
								<option key={r.slug} value={r.slug}>
									{r.label}
								</option>
							))}
						</select>
					</label>
					<button type="submit" disabled={!valid || missingRoles > 0 || busy} className={button.primary}>
						{busy ? "Scouting…" : "Scout team"}
					</button>
					{valid && missingRoles > 0 && (
						<span className="text-[13px] text-ink-2">
							Set a role for {missingRoles === 1 ? "1 more player" : `${missingRoles} more players`}: only their games on that role count
						</span>
					)}
				</div>
			</form>

			{error && (
				<div role="alert" className="mt-5 max-w-[640px] rounded-[10px] border border-line-2 bg-raised px-3.5 py-3 text-sm text-ink">
					{error}
				</div>
			)}
			{busy && (
				<div aria-busy="true" className="mt-6 flex flex-col gap-3">
					<p role="status" className="m-0 text-[13px] text-ink-2">
						{progress}… A team we have not seen takes up to a minute.
					</p>
					<Skeleton className="h-[220px] rounded-[10px]" />
					<Skeleton className="h-[160px] rounded-[10px]" />
				</div>
			)}
			{!busy && result && <Result data={result} />}
		</section>
	);
}
